// src/components/AuthorGrid.jsx
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Grid as GridIcon, List as ListIcon } from 'lucide-react';
import AuthorCard from '../homePageComponents/AuthorCard';
import SkeletonCard from '../uiComponents/SkeletonCard';
import AuthorListItem from '../homePageComponents/AuthorList';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 15;

export default function AuthorGrid({ authors = [], view, onViewChange, loading = false }) {
  const placeholderCount = PAGE_SIZE;
  const navigate = useNavigate();

  const handleAuthorClick = (authorId) => {
    navigate(`/author/${authorId}`);
  };

  return (
    <div className="bg-primary-shade1 p-4 rounded-lg">
      {/* Conditionally show view toggle only if onViewChange is provided */}
      {onViewChange && (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => onViewChange('grid')}
            className={`p-2 rounded-md mr-2 transition ${
              view === 'grid' ? 'bg-[#FF7236] text-white' : 'text-[#FF7236] hover:bg-[#FFD8C7]'
            }`}
          >
            <GridIcon size={20} />
          </button>
          <button
            onClick={() => onViewChange('list')}
            className={`p-2 rounded-md transition ${
              view === 'list' ? 'bg-[#FF7236] text-white' : 'text-[#FF7236] hover:bg-[#FFD8C7]'
            }`}
          >
            <ListIcon size={20} />
          </button>
        </div>
      )}

      {/* Grid View */}
      {view === 'grid' ? (
        loading ? (
          <div className="flex justify-center">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6 lg:gap-8">
              {Array.from({ length: placeholderCount }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        ) : (
          <AnimatePresence>
            <div className="flex justify-center">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6 lg:gap-8">
                {authors.map((author, i) => (
                  <motion.div
                    key={author.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ delay: i * 0.05, type: 'spring', stiffness: 100 }}
                  >
                    <div onClick={() => handleAuthorClick(author.id)}>
                      <AuthorCard name={author.name} image={author.image} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </AnimatePresence>
        )
      ) : (
        // List View
        <AnimatePresence>
          <div className="space-y-4">
            {loading
              ? Array.from({ length: placeholderCount }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))
              : authors.map((author, i) => (
                  <motion.div
                    key={author.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: i * 0.03, type: 'spring', stiffness: 120 }}
                  >
                    <div onClick={() => handleAuthorClick(author.id)}>
                      <AuthorListItem author={author} />
                    </div>
                  </motion.div>
                ))}
          </div>
        </AnimatePresence>
      )}
    </div>
  );
}
